import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Building2, ArrowLeft, ArrowRight, Check, Loader2,
  User, Mail, Lock, Globe, Clock, Shield, Box, Zap,
  Database, MessageSquare, BarChart3, Webhook, Brain, Key,
  Crown, Star, Sparkles, Award, Copy, Info, CheckCircle, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../api/client';

const STEPS = [
  { id: 'basic', title: 'Basic Info', description: 'Tenant name, region & tier' },
  { id: 'modules', title: 'Modules', description: 'Select enabled features' },
  { id: 'admin', title: 'Admin User', description: 'Create first admin' },
  { id: 'confirm', title: 'Confirm', description: 'Review and create' }
];

// Tier configuration with default modules and limits
const TIER_CONFIG = {
  starter: {
    name: 'Starter',
    description: 'Essential features for small teams',
    icon: Star,
    color: 'gray',
    maxUsers: 25,
    maxProjects: 10,
    defaultModules: ['scope_requirements'],
    features: ['Basic Scoping', 'Requirements Capture', 'CSV Export']
  },
  business: {
    name: 'Business',
    description: 'Full platform for growing organizations',
    icon: Crown,
    color: 'blue',
    maxUsers: 100,
    maxProjects: 50,
    defaultModules: ['scope_requirements', 'baseline_management', 'governance_flow', 'reporting'],
    features: ['All Starter Features', 'Baselines & WBS', 'Basic Governance', 'AI Copilot (Partial)', 'Jira/ADO Sync']
  },
  enterprise: {
    name: 'Enterprise',
    description: 'Advanced capabilities with full integrations',
    icon: Sparkles,
    color: 'purple',
    maxUsers: 250,
    maxProjects: 200,
    defaultModules: ['scope_requirements', 'baseline_management', 'governance_flow', 'reporting', 'integrations', 'ai_assistance'],
    features: ['All Business Features', 'AI Copilot (Full)', 'Full Bi-Directional Sync', 'SSO/MFA', 'Audit Logs', '99.9% SLA']
  },
  enterprise_plus: {
    name: 'Enterprise+',
    description: 'Custom deployment with dedicated support',
    icon: Award,
    color: 'amber',
    maxUsers: 1000,
    maxProjects: 'Unlimited',
    defaultModules: ['scope_requirements', 'baseline_management', 'governance_flow', 'reporting', 'integrations', 'ai_assistance', 'api_access'],
    features: ['All Enterprise Features', 'Advanced AI Agents', 'API Access', 'Data Residency', 'Hybrid/On-Prem', 'Solution Architect', '24/7 Support']
  }
};

const AVAILABLE_MODULES = [
  { key: 'scope_requirements', name: 'Scope Requirements', description: 'Requirements hierarchy management', icon: Box, tier: 'starter' },
  { key: 'baseline_management', name: 'Baseline Management', description: 'WBS baselines and versioning', icon: Database, tier: 'starter' },
  { key: 'governance_flow', name: 'Governance & Approvals', description: 'Workflow approvals and change control', icon: Shield, tier: 'business' },
  { key: 'reporting', name: 'Reporting Suite', description: 'Dashboards and analytics', icon: BarChart3, tier: 'business' },
  { key: 'integrations', name: 'Integrations', description: 'ADO, Jira, SSO connectivity', icon: Webhook, tier: 'enterprise' },
  { key: 'ai_assistance', name: 'AI Assistance', description: 'AI-powered scoping and suggestions', icon: Brain, tier: 'business' },
  { key: 'api_access', name: 'API Access', description: 'REST API for custom integrations', icon: Key, tier: 'enterprise_plus' },
];

const TIMEZONES = [
  { value: 'UTC', label: 'UTC' },
  { value: 'America/New_York', label: 'Eastern Time (US)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (US)' },
  { value: 'Europe/London', label: 'London (UK)' },
  { value: 'Europe/Paris', label: 'Central European Time' },
  { value: 'Asia/Singapore', label: 'Singapore' },
  { value: 'Asia/Tokyo', label: 'Tokyo (Japan)' },
  { value: 'Australia/Sydney', label: 'Sydney (Australia)' },
  { value: 'Africa/Johannesburg', label: 'Johannesburg (South Africa)' },
];

const REGIONS = [
  { value: 'us', label: 'United States' },
  { value: 'eu', label: 'European Union' },
  { value: 'uk', label: 'United Kingdom' },
  { value: 'au', label: 'Australia' },
  { value: 'za', label: 'South Africa' },
  { value: 'sg', label: 'Singapore' },
  { value: 'other', label: 'Other' },
];

const CreateTenantPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  
  // Clone mode state
  const [cloneMode, setCloneMode] = useState(false);
  const [availableTenants, setAvailableTenants] = useState([]);
  const [sourceTenant, setSourceTenant] = useState(null);
  const [loadingTenants, setLoadingTenants] = useState(false);
  const [loadingSourceConfig, setLoadingSourceConfig] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    // Basic Info
    name: '',
    slug: '',
    region: 'us',
    timezone: 'UTC',
    tier: 'business', // Default tier
    
    // Modules (will be populated based on tier)
    enabledModules: TIER_CONFIG.business.defaultModules,
    
    // Security config (for clone mode)
    securityConfig: null,
    
    // Admin User
    adminName: '',
    adminEmail: '',
    adminPassword: '',
    sendInvite: true
  });
  
  // Track if user has manually modified modules (to avoid overwriting on tier change)
  const [modulesManuallyModified, setModulesManuallyModified] = useState(false);

  // Check for clone parameter in URL
  useEffect(() => {
    const cloneFromId = searchParams.get('clone');
    if (cloneFromId) {
      setCloneMode(true);
      loadSourceTenantConfig(cloneFromId);
    }
  }, [searchParams]);

  // Load available tenants when clone mode is enabled
  useEffect(() => {
    if (cloneMode && availableTenants.length === 0) {
      loadAvailableTenants();
    }
  }, [cloneMode]);

  const loadAvailableTenants = async () => {
    setLoadingTenants(true);
    try {
      const response = await api.listTenants({ limit: 200 });
      setAvailableTenants(response.data.tenants || []);
    } catch (err) {
      console.error('Failed to load tenants:', err);
      toast.error('Failed to load tenants for cloning');
    } finally {
      setLoadingTenants(false);
    }
  };

  const loadSourceTenantConfig = async (tenantId) => {
    setLoadingSourceConfig(true);
    try {
      // Get tenant details
      const tenantResponse = await api.getTenant(tenantId);
      const tenantData = tenantResponse.data;
      
      // Get tenant config (features, security)
      const configResponse = await api.getTenantConfig(tenantId);
      const config = configResponse.data;
      
      const sourceTier = config.tier || tenantData.billing?.tier || 'starter';
      const sourceFeatures = config.features || {};
      const enabledModuleKeys = Object.keys(sourceFeatures).filter(k => sourceFeatures[k]);
      
      setSourceTenant({
        id: tenantId,
        name: tenantData.tenant?.name || 'Unknown',
        tier: sourceTier,
        features: sourceFeatures,
        security_config: config.security_config || {},
        wbs_schema: tenantData.tenant?.wbs_schema,
        moduleCount: enabledModuleKeys.length,
        domainCount: (config.security_config?.domain_restrictions || []).length,
        hasMicrosoftSSO: config.security_config?.microsoft_sso_enabled,
        hasSAML: config.security_config?.saml_enabled,
        hasMFA: config.security_config?.mfa_enabled
      });
      
      // Pre-populate form with source data
      setFormData(prev => ({
        ...prev,
        tier: sourceTier,
        enabledModules: enabledModuleKeys.length > 0 ? enabledModuleKeys : TIER_CONFIG[sourceTier]?.defaultModules || [],
        securityConfig: config.security_config
      }));
      
      setModulesManuallyModified(true); // Prevent tier change from overwriting
      
    } catch (err) {
      console.error('Failed to load source tenant config:', err);
      toast.error('Failed to load source tenant configuration');
    } finally {
      setLoadingSourceConfig(false);
    }
  };

  const updateForm = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Auto-generate slug from name
    if (field === 'name') {
      const slug = value.toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .slice(0, 30);
      setFormData(prev => ({ ...prev, slug }));
    }
    
    // When tier changes in non-clone mode, update default modules
    if (field === 'tier' && !modulesManuallyModified && !cloneMode) {
      const tierDefaults = TIER_CONFIG[value]?.defaultModules || [];
      setFormData(prev => ({ ...prev, enabledModules: tierDefaults }));
    }
  };
  
  const handleTierChange = (newTier) => {
    // When explicitly changing tier, reset to tier defaults (unless cloning)
    if (!cloneMode || !sourceTenant) {
      const tierDefaults = TIER_CONFIG[newTier]?.defaultModules || [];
      setFormData(prev => ({ 
        ...prev, 
        tier: newTier,
        enabledModules: tierDefaults 
      }));
      setModulesManuallyModified(false);
    } else {
      // In clone mode, just change the tier but keep modules
      setFormData(prev => ({ ...prev, tier: newTier }));
    }
  };

  const toggleModule = (moduleKey) => {
    setModulesManuallyModified(true);
    setFormData(prev => ({
      ...prev,
      enabledModules: prev.enabledModules.includes(moduleKey)
        ? prev.enabledModules.filter(m => m !== moduleKey)
        : [...prev.enabledModules, moduleKey]
    }));
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0: // Basic Info
        return formData.name.trim().length >= 3 && formData.slug.trim().length >= 3 && formData.tier;
      case 1: // Modules
        return formData.enabledModules.length > 0;
      case 2: // Admin User
        return (
          formData.adminName.trim().length >= 2 &&
          /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.adminEmail) &&
          (formData.sendInvite || formData.adminPassword.length >= 8)
        );
      case 3: // Confirm
        return true;
      default:
        return false;
    }
  };

  const handleCreate = async () => {
    setLoading(true);
    try {
      // Build features object from enabled modules
      const features = {};
      AVAILABLE_MODULES.forEach(mod => {
        features[mod.key] = formData.enabledModules.includes(mod.key);
      });
      
      let response;
      
      if (cloneMode && sourceTenant) {
        // Use clone endpoint
        response = await api.cloneTenant({
          source_tenant_id: sourceTenant.id,
          name: formData.name.trim(),
          slug: formData.slug.trim(),
          region: formData.region,
          timezone: formData.timezone,
          tier_override: formData.tier !== sourceTenant.tier ? formData.tier : null,
          features_override: features,
          security_config_override: formData.securityConfig,
          admin_user: {
            name: formData.adminName.trim(),
            email: formData.adminEmail.trim(),
            password: formData.sendInvite ? undefined : formData.adminPassword,
            send_invite: formData.sendInvite
          }
        });
        
        toast.success(`Tenant "${formData.name}" cloned successfully from "${sourceTenant.name}"!`);
      } else {
        // Use regular create endpoint
        response = await api.createTenant({
          name: formData.name.trim(),
          slug: formData.slug.trim(),
          country: formData.region.toUpperCase(),
          timezone: formData.timezone,
          tier: formData.tier,
          is_active: true,
          features,
          product_config: {
            features,
            tier: formData.tier
          },
          security_config: {
            password_enabled: true,
            microsoft_sso_enabled: false,
            saml_enabled: false,
            domain_restrictions: []
          }
        });
        
        const tenantId = response.data.tenant_id || response.data.id;
        
        // Create admin user
        if (tenantId) {
          try {
            await api.createAdminUser({
              tenant_id: tenantId,
              name: formData.adminName.trim(),
              email: formData.adminEmail.trim(),
              password: formData.sendInvite ? undefined : formData.adminPassword,
              role: 'admin',
              send_invite: formData.sendInvite
            });
          } catch (userErr) {
            console.warn('Admin user creation may have failed:', userErr);
            toast.warning('Tenant created, but admin user setup may require manual configuration.');
          }
        }
        
        toast.success(`Tenant "${formData.name}" created successfully!`);
      }
      
      navigate('/platform/tenants');
      
    } catch (err) {
      console.error('Failed to create tenant:', err);
      toast.error(err.response?.data?.detail || 'Failed to create tenant');
    } finally {
      setLoading(false);
    }
  };

  // Get tier badge color
  const getTierColor = (tier) => {
    const colors = {
      starter: 'bg-gray-100 text-gray-700 border-gray-200',
      business: 'bg-blue-100 text-blue-700 border-blue-200',
      enterprise: 'bg-purple-100 text-purple-700 border-purple-200',
      enterprise_plus: 'bg-amber-100 text-amber-700 border-amber-200'
    };
    return colors[tier] || colors.starter;
  };
  
  const getTierBgColor = (tier, selected) => {
    if (!selected) return 'bg-white border-gray-200 hover:border-gray-300';
    const colors = {
      starter: 'bg-gray-50 border-gray-400 ring-2 ring-gray-400',
      business: 'bg-blue-50 border-blue-500 ring-2 ring-blue-500',
      enterprise: 'bg-purple-50 border-purple-500 ring-2 ring-purple-500',
      enterprise_plus: 'bg-amber-50 border-amber-500 ring-2 ring-amber-500'
    };
    return colors[tier] || colors.starter;
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="space-y-6">
            {/* Clone Mode Toggle */}
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="createMode"
                    checked={!cloneMode}
                    onChange={() => {
                      setCloneMode(false);
                      setSourceTenant(null);
                      setModulesManuallyModified(false);
                      setFormData(prev => ({
                        ...prev,
                        tier: 'business',
                        enabledModules: TIER_CONFIG.business.defaultModules,
                        securityConfig: null
                      }));
                    }}
                    className="w-4 h-4 text-indigo-600"
                    data-testid="mode-fresh"
                  />
                  <span className="font-medium text-gray-700">Start fresh</span>
                </label>
                
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="createMode"
                    checked={cloneMode}
                    onChange={() => setCloneMode(true)}
                    className="w-4 h-4 text-indigo-600"
                    data-testid="mode-clone"
                  />
                  <span className="font-medium text-gray-700 flex items-center gap-1">
                    <Copy className="w-4 h-4" />
                    Clone existing tenant
                  </span>
                </label>
              </div>
            </div>
            
            {/* Source Tenant Selection (Clone Mode) */}
            {cloneMode && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Copy className="w-4 h-4 inline mr-1" />
                    Source Tenant *
                  </label>
                  <select
                    value={sourceTenant?.id || ''}
                    onChange={(e) => {
                      if (e.target.value) {
                        loadSourceTenantConfig(e.target.value);
                      } else {
                        setSourceTenant(null);
                      }
                    }}
                    disabled={loadingTenants || loadingSourceConfig}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    data-testid="source-tenant-select"
                  >
                    <option value="">Select tenant to clone...</option>
                    {availableTenants.map(t => (
                      <option key={t.id} value={t.id}>
                        {t.name} ({TIER_CONFIG[t.billing?.tier]?.name || 'Unknown'})
                      </option>
                    ))}
                  </select>
                </div>
                
                {loadingSourceConfig && (
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading source configuration...
                  </div>
                )}
                
                {/* Clone Preview */}
                {sourceTenant && !loadingSourceConfig && (
                  <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
                    <h4 className="font-medium text-indigo-900 mb-2 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      Cloning from: {sourceTenant.name}
                    </h4>
                    <div className="grid grid-cols-2 gap-2 text-sm text-indigo-700">
                      <div className="flex items-center gap-1">
                        <Check className="w-3 h-3" />
                        Tier: {TIER_CONFIG[sourceTenant.tier]?.name}
                      </div>
                      <div className="flex items-center gap-1">
                        <Check className="w-3 h-3" />
                        {sourceTenant.moduleCount} modules enabled
                      </div>
                      {sourceTenant.hasMicrosoftSSO && (
                        <div className="flex items-center gap-1">
                          <Check className="w-3 h-3" />
                          Microsoft SSO
                        </div>
                      )}
                      {sourceTenant.hasSAML && (
                        <div className="flex items-center gap-1">
                          <Check className="w-3 h-3" />
                          SAML 2.0
                        </div>
                      )}
                      {sourceTenant.hasMFA && (
                        <div className="flex items-center gap-1">
                          <Check className="w-3 h-3" />
                          MFA enabled
                        </div>
                      )}
                      {sourceTenant.domainCount > 0 && (
                        <div className="flex items-center gap-1">
                          <Check className="w-3 h-3" />
                          {sourceTenant.domainCount} domain restriction(s)
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-indigo-600 mt-2">
                      You can modify these in the next steps.
                    </p>
                  </div>
                )}
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tenant Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => updateForm('name', e.target.value)}
                placeholder="e.g., Acme Corporation"
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                data-testid="tenant-name-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tenant Key / Slug *
              </label>
              <div className="flex items-center gap-2">
                <span className="text-gray-400">tenant-</span>
                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) => updateForm('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                  placeholder="acme-corp"
                  className="flex-1 px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent font-mono"
                  data-testid="tenant-slug-input"
                />
              </div>
              <p className="text-xs text-gray-400 mt-1">Used for URLs and API identification</p>
            </div>
            
            {/* Tier Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                <Crown className="w-4 h-4 inline mr-1" />
                Subscription Tier *
                {cloneMode && sourceTenant && (
                  <span className="ml-2 text-xs font-normal text-indigo-600">
                    (from source: {TIER_CONFIG[sourceTenant.tier]?.name})
                  </span>
                )}
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {Object.entries(TIER_CONFIG).map(([tierKey, tierInfo]) => {
                  const TierIcon = tierInfo.icon;
                  const isSelected = formData.tier === tierKey;
                  const isSourceTier = cloneMode && sourceTenant?.tier === tierKey;
                  
                  return (
                    <div
                      key={tierKey}
                      onClick={() => handleTierChange(tierKey)}
                      className={`p-4 border rounded-lg cursor-pointer transition-all ${getTierBgColor(tierKey, isSelected)}`}
                      data-testid={`tier-${tierKey}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          isSelected ? getTierColor(tierKey).replace('bg-', 'bg-').split(' ')[0] : 'bg-gray-100'
                        }`}>
                          <TierIcon className={`w-5 h-5 ${
                            tierKey === 'starter' ? 'text-gray-600' :
                            tierKey === 'business' ? 'text-blue-600' :
                            tierKey === 'enterprise' ? 'text-purple-600' :
                            'text-amber-600'
                          }`} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <h4 className="font-semibold text-gray-900">{tierInfo.name}</h4>
                              {isSourceTier && (
                                <span className="px-1.5 py-0.5 text-xs rounded bg-indigo-100 text-indigo-700">
                                  Source
                                </span>
                              )}
                            </div>
                            {isSelected && (
                              <div className="w-5 h-5 rounded-full bg-indigo-600 flex items-center justify-center">
                                <Check className="w-3 h-3 text-white" />
                              </div>
                            )}
                          </div>
                          <p className="text-sm text-gray-500 mt-0.5">{tierInfo.description}</p>
                          <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                            <span>{tierInfo.maxUsers} users</span>
                            <span>{tierInfo.maxProjects} projects</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {cloneMode 
                  ? 'You can override the source tier. Modules will be preserved unless you reset them.'
                  : 'Tier selection determines default modules and platform limits. You can modify modules in the next step.'
                }
              </p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Globe className="w-4 h-4 inline mr-1" />
                  Region
                </label>
                <select
                  value={formData.region}
                  onChange={(e) => updateForm('region', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  data-testid="tenant-region-select"
                >
                  {REGIONS.map(r => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Clock className="w-4 h-4 inline mr-1" />
                  Timezone
                </label>
                <select
                  value={formData.timezone}
                  onChange={(e) => updateForm('timezone', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  data-testid="tenant-timezone-select"
                >
                  {TIMEZONES.map(tz => (
                    <option key={tz.value} value={tz.value}>{tz.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        );
        
      case 1:
        const selectedTierConfig = TIER_CONFIG[formData.tier];
        return (
          <div className="space-y-4">
            {/* Tier/Clone indicator */}
            <div className={`p-3 rounded-lg border ${
              cloneMode && sourceTenant 
                ? 'bg-indigo-50 border-indigo-200 text-indigo-700'
                : getTierColor(formData.tier)
            }`}>
              <div className="flex items-center gap-2">
                {cloneMode && sourceTenant ? (
                  <>
                    <Copy className="w-4 h-4" />
                    <span className="font-medium">Cloned from: {sourceTenant.name}</span>
                  </>
                ) : (
                  <>
                    {React.createElement(selectedTierConfig.icon, { className: 'w-4 h-4' })}
                    <span className="font-medium">{selectedTierConfig.name} Tier Selected</span>
                  </>
                )}
              </div>
              <p className="text-sm mt-1 opacity-75">
                {cloneMode && sourceTenant
                  ? `Modules are pre-populated from ${sourceTenant.name}. You can customize as needed.`
                  : 'Default modules for this tier are pre-selected. You can customize as needed.'
                }
              </p>
            </div>
            
            <p className="text-sm text-gray-600">
              Select which modules to enable for this tenant.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {AVAILABLE_MODULES.map((mod) => {
                const IconComponent = mod.icon;
                const isEnabled = formData.enabledModules.includes(mod.key);
                const isDefaultForTier = selectedTierConfig.defaultModules.includes(mod.key);
                const isFromSource = cloneMode && sourceTenant?.features?.[mod.key];
                
                return (
                  <div
                    key={mod.key}
                    onClick={() => toggleModule(mod.key)}
                    className={`p-4 border rounded-lg cursor-pointer transition-all ${
                      isEnabled 
                        ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-500' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid={`module-${mod.key}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        isEnabled ? 'bg-indigo-100' : 'bg-gray-100'
                      }`}>
                        <IconComponent className={`w-5 h-5 ${isEnabled ? 'text-indigo-600' : 'text-gray-500'}`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between flex-wrap gap-1">
                          <h4 className="font-medium text-gray-900">{mod.name}</h4>
                          <div className="flex items-center gap-1">
                            {isFromSource && (
                              <span className="px-1.5 py-0.5 text-xs rounded bg-indigo-100 text-indigo-700">
                                From Clone
                              </span>
                            )}
                            {!cloneMode && isDefaultForTier && (
                              <span className="px-1.5 py-0.5 text-xs rounded bg-green-100 text-green-700">
                                Tier Default
                              </span>
                            )}
                            <span className={`px-2 py-0.5 text-xs rounded-full ${
                              mod.tier === 'starter' ? 'bg-gray-100 text-gray-600' :
                              mod.tier === 'business' ? 'bg-blue-100 text-blue-600' :
                              mod.tier === 'enterprise' ? 'bg-purple-100 text-purple-600' :
                              'bg-amber-100 text-amber-600'
                            }`}>
                              {mod.tier === 'enterprise_plus' ? 'E+' : mod.tier.charAt(0).toUpperCase() + mod.tier.slice(1)}
                            </span>
                          </div>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">{mod.description}</p>
                      </div>
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                        isEnabled ? 'bg-indigo-600 border-indigo-600' : 'border-gray-300'
                      }`}>
                        {isEnabled && <Check className="w-3 h-3 text-white" />}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            <div className="mt-4 p-3 bg-gray-50 rounded-lg text-sm text-gray-600 flex items-center justify-between">
              <span><strong>{formData.enabledModules.length}</strong> module(s) selected</span>
              {(modulesManuallyModified || cloneMode) && (
                <button
                  onClick={() => {
                    if (cloneMode && sourceTenant) {
                      const sourceModules = Object.keys(sourceTenant.features || {}).filter(k => sourceTenant.features[k]);
                      setFormData(prev => ({ ...prev, enabledModules: sourceModules }));
                    } else {
                      handleTierChange(formData.tier);
                    }
                  }}
                  className="text-indigo-600 hover:text-indigo-700 text-sm"
                >
                  {cloneMode ? 'Reset to source config' : 'Reset to tier defaults'}
                </button>
              )}
            </div>
          </div>
        );
        
      case 2:
        return (
          <div className="space-y-6">
            <p className="text-sm text-gray-600 mb-4">
              Create the first administrator for this tenant. They will have full access to configure the tenant.
            </p>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <User className="w-4 h-4 inline mr-1" />
                Admin Name *
              </label>
              <input
                type="text"
                value={formData.adminName}
                onChange={(e) => updateForm('adminName', e.target.value)}
                placeholder="John Smith"
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
                data-testid="admin-name-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Mail className="w-4 h-4 inline mr-1" />
                Admin Email *
              </label>
              <input
                type="email"
                value={formData.adminEmail}
                onChange={(e) => updateForm('adminEmail', e.target.value)}
                placeholder="admin@company.com"
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
                data-testid="admin-email-input"
              />
            </div>
            
            <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
              <input
                type="checkbox"
                id="sendInvite"
                checked={formData.sendInvite}
                onChange={(e) => updateForm('sendInvite', e.target.checked)}
                className="w-4 h-4 text-indigo-600 rounded"
              />
              <label htmlFor="sendInvite" className="text-sm text-gray-700">
                Send email invitation (recommended)
              </label>
            </div>
            
            {/* Email Delivery Warning - explicitly state mocked status */}
            {formData.sendInvite && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-900">Email delivery not configured</p>
                    <p className="text-sm text-amber-700 mt-1">
                      Email invitations are currently <strong>not being sent</strong>. The admin user will be created, 
                      but you must manually share login credentials or configure SendGrid integration.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {!formData.sendInvite && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Lock className="w-4 h-4 inline mr-1" />
                  Initial Password *
                </label>
                <input
                  type="password"
                  value={formData.adminPassword}
                  onChange={(e) => updateForm('adminPassword', e.target.value)}
                  placeholder="Minimum 8 characters"
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  data-testid="admin-password-input"
                />
                {formData.adminPassword && formData.adminPassword.length < 8 && (
                  <p className="text-xs text-red-500 mt-1">Password must be at least 8 characters</p>
                )}
              </div>
            )}
          </div>
        );
        
      case 3:
        const tierInfo = TIER_CONFIG[formData.tier];
        const TierIcon = tierInfo.icon;
        return (
          <div className="space-y-6">
            {/* Clone Badge */}
            {cloneMode && sourceTenant && (
              <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
                <div className="flex items-center gap-2">
                  <Copy className="w-5 h-5 text-indigo-600" />
                  <span className="font-semibold text-indigo-900">Cloned from: {sourceTenant.name}</span>
                </div>
                <p className="text-sm text-indigo-700 mt-1">
                  Configuration, modules, and security settings inherited from source tenant.
                </p>
              </div>
            )}
            
            {/* Tenant Details */}
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Tenant Details</h3>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Name:</span>
                  <span className="ml-2 font-medium">{formData.name}</span>
                </div>
                <div>
                  <span className="text-gray-500">Key:</span>
                  <code className="ml-2 px-2 py-0.5 bg-gray-200 rounded text-xs">tenant-{formData.slug}</code>
                </div>
                <div>
                  <span className="text-gray-500">Region:</span>
                  <span className="ml-2">{REGIONS.find(r => r.value === formData.region)?.label}</span>
                </div>
                <div>
                  <span className="text-gray-500">Timezone:</span>
                  <span className="ml-2">{formData.timezone}</span>
                </div>
              </div>
            </div>
            
            {/* Subscription Tier */}
            <div className={`rounded-lg p-6 border ${getTierColor(formData.tier)}`}>
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <TierIcon className="w-5 h-5" />
                Subscription Tier: {tierInfo.name}
                {cloneMode && sourceTenant && formData.tier !== sourceTenant.tier && (
                  <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded">
                    Changed from {TIER_CONFIG[sourceTenant.tier]?.name}
                  </span>
                )}
              </h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Max Users:</span>
                  <span className="ml-2 font-medium">{tierInfo.maxUsers}</span>
                </div>
                <div>
                  <span className="text-gray-600">Max Projects:</span>
                  <span className="ml-2 font-medium">{tierInfo.maxProjects}</span>
                </div>
              </div>
              <div className="mt-3">
                <span className="text-xs text-gray-600">Included Features:</span>
                <div className="flex flex-wrap gap-1 mt-1">
                  {tierInfo.features.slice(0, 4).map((feat, idx) => (
                    <span key={idx} className="text-xs px-2 py-0.5 rounded bg-white/50">
                      {feat}
                    </span>
                  ))}
                  {tierInfo.features.length > 4 && (
                    <span className="text-xs px-2 py-0.5 rounded bg-white/50">
                      +{tierInfo.features.length - 4} more
                    </span>
                  )}
                </div>
              </div>
            </div>
            
            {/* Enabled Modules */}
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Enabled Modules ({formData.enabledModules.length})</h3>
              <div className="flex flex-wrap gap-2">
                {formData.enabledModules.map(modKey => {
                  const mod = AVAILABLE_MODULES.find(m => m.key === modKey);
                  return (
                    <span key={modKey} className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm flex items-center gap-1">
                      {React.createElement(mod?.icon || Box, { className: 'w-3 h-3' })}
                      {mod?.name}
                    </span>
                  );
                })}
              </div>
            </div>
            
            {/* Security Config (Clone mode) */}
            {cloneMode && sourceTenant && formData.securityConfig && (
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-4">Security Config (Cloned)</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {formData.securityConfig.password_enabled && (
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-600" />
                      Email/Password
                    </div>
                  )}
                  {formData.securityConfig.microsoft_sso_enabled && (
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-600" />
                      Microsoft SSO
                    </div>
                  )}
                  {formData.securityConfig.saml_enabled && (
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-600" />
                      SAML 2.0
                    </div>
                  )}
                  {formData.securityConfig.mfa_enabled && (
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-600" />
                      MFA Enabled
                    </div>
                  )}
                  {(formData.securityConfig.domain_restrictions || []).length > 0 && (
                    <div className="flex items-center gap-2 col-span-2">
                      <Check className="w-4 h-4 text-green-600" />
                      {formData.securityConfig.domain_restrictions.length} domain restriction(s)
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Admin User */}
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Admin User</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Name:</span>
                  <span className="ml-2 font-medium">{formData.adminName}</span>
                </div>
                <div>
                  <span className="text-gray-500">Email:</span>
                  <span className="ml-2">{formData.adminEmail}</span>
                </div>
                <div className="col-span-2">
                  <span className="text-gray-500">Setup:</span>
                  <span className="ml-2">{formData.sendInvite ? 'Password set manually (email not configured)' : 'Password set manually'}</span>
                </div>
              </div>
              {formData.sendInvite && (
                <div className="mt-3 p-3 bg-amber-100 border border-amber-200 rounded text-sm text-amber-800">
                  <strong>Note:</strong> Email will NOT be delivered. Share credentials manually.
                </div>
              )}
            </div>
            
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                <strong>Note:</strong> After creation, you can configure additional settings like SSO, 
                WBS schema, and advanced permissions from the tenant's settings page.
              </p>
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/platform/tenants')}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-4"
            data-testid="back-to-tenants"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Tenant Directory
          </button>
          
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              cloneMode 
                ? 'bg-gradient-to-br from-indigo-500 to-blue-600' 
                : 'bg-gradient-to-br from-indigo-500 to-purple-600'
            }`}>
              {cloneMode ? (
                <Copy className="w-6 h-6 text-white" />
              ) : (
                <Building2 className="w-6 h-6 text-white" />
              )}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {cloneMode ? 'Clone Tenant' : 'Create New Tenant'}
              </h1>
              <p className="text-sm text-gray-500">
                {cloneMode 
                  ? 'Duplicate configuration from an existing tenant'
                  : 'Set up a new isolated tenant environment'
                }
              </p>
            </div>
          </div>
        </div>
        
        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {STEPS.map((step, index) => (
              <React.Fragment key={step.id}>
                <div className="flex items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-medium ${
                    index < currentStep ? 'bg-green-500 text-white' :
                    index === currentStep ? 'bg-indigo-600 text-white' :
                    'bg-gray-200 text-gray-500'
                  }`}>
                    {index < currentStep ? <Check className="w-5 h-5" /> : index + 1}
                  </div>
                  <div className="ml-3 hidden md:block">
                    <p className={`text-sm font-medium ${index <= currentStep ? 'text-gray-900' : 'text-gray-400'}`}>
                      {step.title}
                    </p>
                    <p className="text-xs text-gray-400">{step.description}</p>
                  </div>
                </div>
                {index < STEPS.length - 1 && (
                  <div className={`flex-1 h-1 mx-4 rounded ${
                    index < currentStep ? 'bg-green-500' : 'bg-gray-200'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
        
        {/* Step Content */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">
            {STEPS[currentStep].title}
          </h2>
          
          {renderStepContent()}
        </div>
        
        {/* Navigation Buttons */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => setCurrentStep(s => Math.max(0, s - 1))}
            disabled={currentStep === 0}
            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50"
            data-testid="prev-step-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Previous
          </button>
          
          {currentStep < STEPS.length - 1 ? (
            <button
              onClick={() => setCurrentStep(s => s + 1)}
              disabled={!canProceed()}
              className="flex items-center gap-2 px-6 py-2.5 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="next-step-btn"
            >
              Next
              <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={handleCreate}
              disabled={loading || !canProceed()}
              className="flex items-center gap-2 px-6 py-2.5 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 disabled:opacity-50"
              data-testid="create-tenant-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {cloneMode ? 'Cloning...' : 'Creating...'}
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  {cloneMode ? 'Clone Tenant' : 'Create Tenant'}
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateTenantPage;
